/**
 * Byte Haus Product Comparison System
 * Handles comparison modal functionality for Shopify products
 */

class ProductComparison {
  constructor() {
    this.modal = document.getElementById('comparisonModal');
    this.productsContainer = document.getElementById('comparisonProducts');
    this.specsTableBody = document.getElementById('specsTableBody');
    
    if (!this.modal) return;
    
    const dataElement = document.getElementById('comparisonData');
    if (!dataElement) return;
    
    try {
      this.comparisonData = JSON.parse(dataElement.textContent);
    } catch (e) {
      console.error('Failed to parse comparison data:', e);
      return;
    }
    
    this.compareState = this.loadState();
    this.init();
  }

  init() {
    if (!this.modal || !this.comparisonData) return;
    
    // Delegate event listeners
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.compare-button');
      if (btn) {
        e.preventDefault();
        this.handleCompareClick(btn);
      }
    });

    this.modal.querySelector('.comparison-modal__close')?.addEventListener('click', () => this.closeModal());
    this.modal.querySelector('.comparison-modal__overlay')?.addEventListener('click', () => this.closeModal());
    
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.style.display !== 'none') {
        this.closeModal();
      }
    });
  }

  handleCompareClick(btn) {
    const productHandle = btn.dataset.compareProduct;
    const variantId = parseInt(btn.dataset.compareVariant, 10);
    
    if (!productHandle || !variantId) return;
    
    this.compareState = [{ productHandle, variantId }, null, null];
    this.saveState();
    this.openModal();
  }

  openModal() {
    this.modal.style.display = 'block';
    document.body.style.overflow = 'hidden';
    this.render();
  }

  closeModal() {
    this.modal.style.display = 'none';
    document.body.style.overflow = '';
  }

  render() {
    this.renderProducts();
    this.renderSpecs();
  }

  renderProducts() {
    // Ensure exactly 3 slots
    this.compareState = [...this.compareState.slice(0, 3), null, null, null].slice(0, 3);
    this.productsContainer.innerHTML = '';

    this.compareState.forEach((item, index) => {
      if (item) {
        const product = this.getProductByHandle(item.productHandle);
        const variant = this.getVariantById(product, item.variantId);
        if (product && variant) {
          this.productsContainer.appendChild(this.createProductColumn(product, variant, index));
        }
      } else {
        this.productsContainer.appendChild(this.createEmptyColumn(index));
      }
    });
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  createProductColumn(product, variant, index) {
    const col = document.createElement('div');
    col.className = 'product-column';

    const image = this.escapeHtml(variant.image || product.featuredImage || '');
    const title = this.escapeHtml(product.title);
    const sku = this.escapeHtml(variant.sku);
    const productUrl = `${product.url}?variant=${variant.id}`;

    col.innerHTML = `
      <button class="product-column__remove" data-index="${index}" aria-label="Remove product">×</button>
      <img src="${image}" alt="${title}" class="product-column__image">
      <div class="product-column__content">
        <a href="${productUrl}" class="product-column__title" target="_blank">${title}</a>
        <div class="product-column__sku">${sku}</div>
        ${this.createProductSelect(product, index)}
        ${this.createVariantSelects(product, variant, index)}
        <form class="product-form-comparison-${index}" data-product-id="${product.id}">
          <input type="hidden" name="id" value="${variant.id}">
          <button type="button" 
                  class="product-column__add-to-cart product-form__quote button button--primary rfq-btn" 
                  id="rfq-btn-comparison-${index}"
                  data-product-id="${product.id}"
                  onclick="event.stopPropagation();GRFQApp.addProduct(this,${product.id})">
            <span>Add to Quote</span>
            <div class="loading-overlay__spinner hidden">
              <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
              </svg>
            </div>
          </button>
        </form>
      </div>
    `;

    col.querySelector('.product-column__remove').addEventListener('click', () => this.removeProduct(index));
    col.querySelector('.product-select').addEventListener('change', (e) => this.changeProduct(index, e.target.value));
    col.querySelectorAll('.variant-select').forEach((select) => {
      select.addEventListener('change', (e) => this.changeVariant(index, e.target));
    });

    return col;
  }

  createEmptyColumn(index) {
    const col = document.createElement('div');
    col.className = 'product-column product-column--empty';
    col.innerHTML = `
      <div class="product-column__image product-column__image--empty">
        <span class="product-column__empty-icon">?</span>
      </div>
      <div class="product-column__content">
        <div class="product-column__title product-column__title--empty">Select a product</div>
        ${this.createProductSelect(null, index)}
      </div>
    `;

    col.querySelector('.product-select').addEventListener('change', (e) => this.changeProduct(index, e.target.value));
    return col;
  }

  createProductSelect(currentProduct, index) {
    const options = this.comparisonData.products.map(p => {
      const selected = currentProduct && p.handle === currentProduct.handle ? 'selected' : '';
      const title = this.escapeHtml(p.title);
      return `<option value="${this.escapeHtml(p.handle)}" ${selected}>${title}</option>`;
    }).join('');

    return `
      <select class="product-column__select product-select">
        <option value="">Select Product</option>
        ${options}
      </select>
    `;
  }

  createVariantSelects(product, currentVariant, index) {
    if (!product.options || product.options.length === 0) return '';

    // Exclude last option (form factor)
    return product.options.slice(0, -1).map((option, optionIndex) => {
      const currentValue = currentVariant[`option${optionIndex + 1}`];
      
      // Sort values - if it's capacity (option1), sort numerically
      let sortedValues = [...option.values];
      if (optionIndex === 0) {
        // Assume option1 is capacity - sort numerically
        sortedValues.sort((a, b) => {
          const numA = parseFloat(a.replace(/[^0-9.]/g, ''));
          const numB = parseFloat(b.replace(/[^0-9.]/g, ''));
          return numA - numB;
        });
      }
      
      const options = sortedValues.map(value => {
        const selected = value === currentValue ? 'selected' : '';
        const escapedValue = this.escapeHtml(value);
        return `<option value="${escapedValue}" ${selected}>${escapedValue}</option>`;
      }).join('');

      return `
        <select class="product-column__select variant-select" data-option-index="${optionIndex}">
          ${options}
        </select>
      `;
    }).join('');
  }

  renderSpecs() {
    this.specsTableBody.innerHTML = '';
    const allSpecs = this.collectAllSpecs();
    
    allSpecs.forEach(({ section, specName, normalizedName }) => {
      const row = document.createElement('tr');
      
      if (!specName) {
        // Section header
        row.className = 'spec-group';
        const labelCell = document.createElement('th');
        labelCell.textContent = section;
        labelCell.colSpan = 4;
        row.appendChild(labelCell);
        this.specsTableBody.appendChild(row);
        return;
      }
      
      const labelCell = document.createElement('th');
      labelCell.textContent = specName;
      row.appendChild(labelCell);

      let hasAnyValue = false;
      const cells = this.compareState.map(item => {
        const cell = document.createElement('td');
        cell.setAttribute('data-label', specName);
        
        if (item) {
          const product = this.getProductByHandle(item.productHandle);
          const variant = this.getVariantById(product, item.variantId);
          const value = this.getSpecValue(product, variant, section, specName, normalizedName);
          
          if (value && value !== '-' && value !== '') {
            hasAnyValue = true;
          }
          cell.textContent = value || '-';
        } else {
          cell.textContent = '-';
        }
        return cell;
      });

      if (hasAnyValue) {
        cells.forEach(cell => row.appendChild(cell));
        this.specsTableBody.appendChild(row);
      }
    });
  }

  collectAllSpecs() {
    const specsMap = new Map();
    
    this.compareState.forEach(item => {
      if (!item) return;
      
      const product = this.getProductByHandle(item.productHandle);
      const variant = this.getVariantById(product, item.variantId);
      if (!product?.productSpecifications) return;
      
      try {
        const specs = typeof product.productSpecifications === 'string' 
          ? JSON.parse(product.productSpecifications) 
          : product.productSpecifications;
        
        const formFactor = this.findFormFactor(specs, variant);
        if (!formFactor?.sections) return;
        
        formFactor.sections.forEach(section => {
          if (!specsMap.has(section.title)) {
            specsMap.set(section.title, new Map());
          }
          
          section.rows.forEach(row => {
            const normalizedName = this.normalizeSpecName(row.name);
            const sectionSpecs = specsMap.get(section.title);
            
            if (!sectionSpecs.has(normalizedName)) {
              sectionSpecs.set(normalizedName, {
                displayName: row.name,
                originalNames: new Set([row.name])
              });
            } else {
              sectionSpecs.get(normalizedName).originalNames.add(row.name);
            }
          });
        });
      } catch (e) {
        console.error('Error parsing product specifications:', e);
      }
    });
    
    const result = [];
    specsMap.forEach((specNamesMap, sectionTitle) => {
      result.push({ section: sectionTitle, specName: null });
      specNamesMap.forEach((specInfo, normalizedName) => {
        result.push({ 
          section: sectionTitle, 
          specName: specInfo.displayName,
          normalizedName: normalizedName
        });
      });
    });
    
    return result;
  }

  normalizeSpecName(name) {
    return name
      .toLowerCase()
      .replace(/\s*\([^)]*\)/g, '')
      .replace(/\s*\[[^\]]*\]/g, '')
      .replace(/\s+/g, ' ')
      .replace(/\s+(steady\s+state|typical|max|average)$/i, '')
      .trim();
  }

  findFormFactor(specs, variant) {
    if (!specs?.form_factors) return null;
    
    const formFactorOption = variant.option2 || variant.option1;
    return specs.form_factors.find(ff => {
      const ffName = ff.name.toLowerCase();
      const optionName = formFactorOption?.toLowerCase() || '';
      return ffName.includes(optionName) || optionName.includes(ffName);
    });
  }

  getSpecValue(product, variant, sectionTitle, specName, normalizedName) {
    if (!product?.productSpecifications) return null;
    
    try {
      const specs = typeof product.productSpecifications === 'string' 
        ? JSON.parse(product.productSpecifications) 
        : product.productSpecifications;
      
      const formFactor = this.findFormFactor(specs, variant);
      if (!formFactor) return null;
      
      const capacityIndex = formFactor.capacities.indexOf(variant.option1);
      if (capacityIndex === -1) return null;
      
      const section = formFactor.sections.find(s => s.title === sectionTitle);
      if (!section) return null;
      
      let row = section.rows.find(r => r.name === specName);
      if (!row && normalizedName) {
        row = section.rows.find(r => this.normalizeSpecName(r.name) === normalizedName);
      }
      
      if (!row?.values) return null;
      
      let currentIndex = 0;
      for (const valueObj of row.values) {
        const colspan = valueObj.colspan || 1;
        const endIndex = currentIndex + colspan;
        
        if (capacityIndex >= currentIndex && capacityIndex < endIndex) {
          return valueObj.value;
        }
        currentIndex = endIndex;
      }
      
      return null;
    } catch (e) {
      console.error('Error getting spec value:', e);
      return null;
    }
  }

  changeProduct(index, productHandle) {
    if (!productHandle) {
      this.compareState[index] = null;
    } else {
      const product = this.getProductByHandle(productHandle);
      if (!product?.variants?.[0]) return;
      
      this.compareState[index] = {
        productHandle: product.handle,
        variantId: product.variants[0].id
      };
    }
    
    this.saveState();
    this.render();
    this.reinitGRFQApp();
  }

  changeVariant(index, changedSelect = null) {
    const col = this.productsContainer.children[index];
    const product = this.getProductByHandle(this.compareState[index].productHandle);
    if (!product) return;
    
    const variantSelects = col.querySelectorAll('.variant-select');
    const selectedOptions = Array.from(variantSelects).map(s => s.value);
    let variant = product.variants.find(v => 
      selectedOptions.every((option, i) => v[`option${i + 1}`] === option)
    );

    // If exact variant doesn't exist, find nearest match
    if (!variant && changedSelect) {
      const changedOptionIndex = parseInt(changedSelect.dataset.optionIndex, 10);
      variant = this.findNearestVariant(product, selectedOptions, changedOptionIndex);
      
      // Update the OTHER select dropdowns (not the one that was just changed)
      if (variant) {
        variantSelects.forEach((select, i) => {
          if (select !== changedSelect) {
            const optionValue = variant[`option${i + 1}`];
            if (select.value !== optionValue) {
              select.value = optionValue;
            }
          }
        });
      }
    }

    if (variant) {
      this.compareState[index].variantId = variant.id;
      this.updateQuoteButton(col, product.id, variant.id);
      
      const image = variant.image || product.featuredImage || '';
      col.querySelector('.product-column__image').src = image;
      col.querySelector('.product-column__sku').textContent = variant.sku;
      col.querySelector('.product-column__title').href = `${product.url}?variant=${variant.id}`;
      
      this.saveState();
      this.renderSpecs();
      this.reinitGRFQApp();
    }
  }

  findNearestVariant(product, selectedOptions, changedOptionIndex) {
    if (!product?.variants) return null;
    
    // Keep the option that was just changed, find nearest match for others
    const changedValue = selectedOptions[changedOptionIndex];
    
    // Find all variants that match the changed option
    const matchingVariants = product.variants.filter(v => 
      v[`option${changedOptionIndex + 1}`] === changedValue
    );
    
    if (matchingVariants.length === 0) {
      // Fallback: if changed option doesn't exist, return first variant
      return product.variants[0];
    }
    
    if (matchingVariants.length === 1) {
      return matchingVariants[0];
    }
    
    // Find the variant that matches the most OTHER options
    let bestMatch = matchingVariants[0];
    let maxMatches = 0;
    
    for (const variant of matchingVariants) {
      let matches = 0;
      for (let i = 0; i < selectedOptions.length; i++) {
        if (i !== changedOptionIndex && variant[`option${i + 1}`] === selectedOptions[i]) {
          matches++;
        }
      }
      if (matches > maxMatches) {
        maxMatches = matches;
        bestMatch = variant;
      }
    }
    
    return bestMatch;
  }

  updateQuoteButton(col, productId, variantId) {
    const hiddenInput = col.querySelector('input[name="id"]');
    if (hiddenInput) {
      hiddenInput.value = variantId;
      col.querySelector('.product-column__add-to-cart')?.setAttribute('data-product-id', productId);
    }
  }

  removeProduct(index) {
    this.compareState[index] = null;
    this.saveState();
    this.render();
  }

  reinitGRFQApp() {
    if (typeof GRFQApp !== 'undefined' && typeof GRFQApp.init === 'function') {
      GRFQApp.init();
    }
  }

  getProductByHandle(handle) {
    return this.comparisonData.products.find(p => p.handle === handle);
  }

  getVariantById(product, variantId) {
    return product?.variants.find(v => v.id === variantId);
  }

  loadState() {
    try {
      const saved = sessionStorage.getItem('productComparison');
      return saved ? JSON.parse(saved) : [null, null, null];
    } catch (e) {
      return [null, null, null];
    }
  }

  saveState() {
    try {
      sessionStorage.setItem('productComparison', JSON.stringify(this.compareState));
    } catch (e) {
      console.error('Failed to save comparison state', e);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new ProductComparison());
} else {
  new ProductComparison();
}